import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useAuth } from '../../context/AuthContext';
import { Icons } from '@shared/constants';
import { useFeatureFlags } from '../../context/RemoteConfigContext';
import { useDemoMode } from '../../demo/DemoContext';
import { usePageHeaderTitle } from '../../context/PageHeaderContext';
import { useNavigateBack } from '@/hooks/useNavigateBack';
import { useIsBottomNavRoute } from './navItems';
import { logError } from '@/lib/log';

const LogoImg = React.memo(() => (
  <img src="/assets/logos/enough_wordmark_green.png" alt="Enough Logo" width={150} height={60} className="max-w-[150px] h-auto object-contain" />
));

export const TopNav: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  // Close menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);
  const { isFeatureEnabled } = useFeatureFlags();
  const navigate = useNavigate();
  const { routePrefix } = useDemoMode();
  const { logout } = useAuth();
  // The page owns this slot everywhere but Home, which publishes no title and
  // so keeps the wordmark. Pages outside the bottom nav also get their back
  // arrow here, left of the title, instead of in their own body.
  const pageTitle = usePageHeaderTitle();
  const isBottomNavRoute = useIsBottomNavRoute();
  const navigateBack = useNavigateBack();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      logError(error, { context: 'logout' });
    }
    setIsOpen(false);
  };

  const menuItems = [
    { label: 'Profile', icon: Icons.User, action: () => {
      setIsOpen(false);
      navigate(`${routePrefix}/profile`);
    }},
    { label: 'Accounts', icon: Icons.Link, action: () => {
      setIsOpen(false);
      navigate(`${routePrefix}/connect`);
    }},
    // Grow lives here; the Pause page holds its bottom-nav slot. The three
    // spending tools are Pause tabs, not menu entries.
    { label: 'Grow', icon: Icons.Leaf, action: () => {
      setIsOpen(false);
      navigate(`${routePrefix}/grow`);
    }},
    { label: 'Goals', icon: Icons.Target, action: () => {
      setIsOpen(false);
      navigate(`${routePrefix}/goals`);
    }},
    ...(isFeatureEnabled('debtManager') ? [{
      label: 'Debt plan', icon: Icons.TrendingDown, action: () => {
        setIsOpen(false);
        navigate(`${routePrefix}/debt`);
      },
    }] : []),
    { label: 'Journal', icon: Icons.BookOpen, action: () => {
      setIsOpen(false);
      navigate(`${routePrefix}/journal`);
    }},
    { label: 'Contact us', icon: Icons.Mail, action: () => {
      setIsOpen(false);
      navigate(`${routePrefix}/contact`);
    }},
    { label: 'Legal', icon: Icons.FileText, action: () => {
      setIsOpen(false);
      navigate(`${routePrefix}/legal`);
    }},
    { label: 'Log out', icon: Icons.LogOut, isDanger: true, action: handleLogout },
  ];

  return (
    <>
      <header className="sticky top-0 z-50 flex items-center justify-between pr-5 py-2 bg-sand transition-all duration-300" style={{ paddingTop: `calc(var(--sat) + 0.375rem)` }}>
        {/* Page title, or the wordmark on Home. pl-8 is the page content
            gutter (the layout's px-4 plus each page's own px-4), so the title —
            or the back arrow, which the negative margin pulls back to that same
            edge — lines up with the content underneath it. */}
        {pageTitle ? (
          <div className="flex min-w-0 items-center gap-1 pl-8 py-2.5">
            {!isBottomNavRoute && (
              <button
                onClick={navigateBack}
                className="p-2 -ml-2 text-growth hover:bg-growth/5 active:bg-growth/10 rounded-full transition-colors"
                aria-label="Go back"
              >
                <Icons.ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <h1 className="truncate font-serif text-xl text-growth">{pageTitle}</h1>
          </div>
        ) : (
          <button onClick={() => navigate(`${routePrefix}/`)} className="relative inline-flex items-center justify-start px-6 py-2.5 cursor-pointer">
            <LogoImg />
          </button>
        )}

        {/* Hamburger */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="p-2.5 text-growth hover:bg-growth/5 active:bg-growth/10 rounded-full transition-all duration-200"
          aria-label="Toggle menu"
        >
          {isOpen ? <Icons.X className="w-6 h-6" /> : <Icons.Menu className="w-6 h-6" />}
        </button>
      </header>

      {/* Overlay & Slide-in Menu */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-growth/20 backdrop-blur-xs animate-[fadeIn_0.2s_ease-out]"
          onClick={() => setIsOpen(false)}
        />
      )}
      <div
        className={`fixed top-0 right-0 z-50 h-full w-72 bg-cloud shadow-2xl shadow-growth/10 border-l border-mist transform transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Menu header with close */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-mist/30" style={{ paddingTop: `calc(var(--sat) + 1rem)` }}>
          <span className="font-serif text-growth text-lg">Menu</span>
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 text-growth hover:bg-growth/5 rounded-full transition-colors"
            aria-label="Close menu"
          >
            <Icons.X className="w-5 h-5" />
          </button>
        </div>

        {/* Menu items */}
        <nav className="py-3 px-3 space-y-0.5">
          {menuItems.map((item, idx) => (
            <button
              key={idx}
              className={`w-full flex items-center gap-3.5 px-4 py-3.5 rounded-2xl transition-all duration-200 group
                ${item.isDanger
                  ? 'text-enough-wildfire hover:bg-enough-wildfire/10'
                  : 'text-growth hover:bg-sand'
                }`}
              onClick={item.action}
            >
              <item.icon className={`w-5 h-5 ${item.isDanger ? 'opacity-100' : 'opacity-70 group-hover:opacity-100'}`} />
              <span className="font-medium text-[15px]">{item.label}</span>
            </button>
          ))}
        </nav>
      </div>
    </>
  );
};
